<script setup lang="ts">
import { defineProps, reactive, computed, ref, watch, onMounted, Ref, inject } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { cn } from '@/lib/utils'
import { Switch } from '@/components/ui/switch'
import { Sheet, SheetClose, SheetContent, SheetDescription, SheetHeader, SheetFooter, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Input } from '@/components/ui/input'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem, SelectGroup } from '@/components/ui/select'
import { DropdownMenu, DropdownMenuContent, DropdownMenuGroup, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog'
import { Separator } from '@/components/ui/separator'

import {
	ChevronDown, PanelLeftClose, PanelLeftOpen, LayoutGrid, List, Home, Plus,
	Paintbrush, Upload, ArrowUpFromLine, FileOutput, Share2, Check, X, Trash2,
	RefreshCw, Copy, MoreHorizontal, CloudUpload, Link, Rss, FolderOpen,
	GalleryVertical, SwatchBook, Search, Loader2, Layers, FolderTree
} from 'lucide-vue-next'

import draggable from 'vuedraggable'
import { useCookies } from '@vueuse/integrations/useCookies'

import MediaThumb from '@/components/MediaThumb.vue'
import MediaList from '@/components/MediaList.vue'
import TreeView from '@/components/TreeView.vue'
import MediaSearchField from '@/components/MediaSearchField.vue'
import Uploader from '@/views/Uploader.vue'
import CustomMetaData from '@/components/CustomMetaData.vue'
import WidenImport from '@/components/WidenImport.vue'

import { Member } from '@/types/Member'
import DateRangePicker from '@/components/DateRangePicker.vue'
import permissions from "@/services/permissions"
import { getPrettyValues, FilterInfoKeys, filterInfo } from "@/data/filters"
import { Filters, defaultFilters } from "@/types/Filters"
import { useGroups } from '@/services/Groups'
import WidenService from "@/services/Widen"
import { useMedia } from '@/services/Media'
import vhostService from '@/services/Vhost'
import { Group } from "@/types/Groups"
import { Media, defaultMedia } from "@/types/Media"
import { TransferDestination } from "@/types/Transfer"
import { useTransfers } from "@/services/Transfers"
import { toast } from 'vue-sonner'

interface Tab {
	id: string;
	title: string;
	settings: { filters: { moderationStatus: string } };
	disabled: number;
}

const props = defineProps<{ filters: Filters }>();
const router = useRouter();
const route = useRoute();
const { checkPermission } = permissions();
const { getGroupTree, groupTree } = useGroups();
const { updateFile: gqlUpdateFile, setModerationStatus, trashFile, queueReconversion } = useMedia();
const { getTabs: gqlGetTabs, getCustomMeta, getCustomMetaProviders, getCustomMetaGroupings } = vhostService();
const cookies = useCookies(['groupMenu', 'newSparkMobileView', 'newSparkTab']);

const tab = ref("0");
const filters = reactive<Filters>({});
const selectedMedia = ref<Media[]>([]);
const mediaView = ref('grid');
const showDialog = ref(false);
const showGroups = ref(false);
const showMediaList = ref(false);
const isDragging = ref(false);
const cloneItem = reactive<Media>(defaultMedia);
const queryTerm = ref('');
const refresh = ref<number>(0);
const widenSource = ref('');
const cloneMode = ref('platform');
const cloneFields = ref([]);
const sheetMode = ref('clone');
const dialogTitle = ref("Error");
const dialogMessage = ref("Error");
const currentCount = ref(0);
const totalCount = ref(0);
const sortOrder = ref('');
const loading = ref(true);
const widenData = ref<Record<string, any>>({});
const manualTitle = ref('')
const manualDescription = ref('')
const manualTags = ref('')
const savedMobileView = cookies.get('newSparkMobileView') as string | undefined
const mobileView = ref<'scroll' | 'swipe'>(savedMobileView === 'swipe' ? 'swipe' : 'scroll')

// Mobile
const mobileGroupsOpen = ref(false)
const mobileSearchOpen = ref(false)
const mobileSearchQuery = ref('')
const mobileSearchResults = ref<Media[]>([])
const mobileSearchLoading = ref(false)
let mobileSearchTimeout: ReturnType<typeof setTimeout> | null = null


const { fetchMedia: searchFetchMedia } = useMedia()

const onMobileSearchInput = (e: Event) => {
	const query = (e.target as HTMLInputElement).value
	mobileSearchQuery.value = query
	if (mobileSearchTimeout) clearTimeout(mobileSearchTimeout)
	if (!query || query.length < 2) {
		mobileSearchResults.value = []
		return
	}
	mobileSearchTimeout = setTimeout(async () => {
		mobileSearchLoading.value = true
		try {
			const fields: (keyof Media)[] = ['id', 'title', 'tags', 'message', 'publicUrl', 'thumbUrl', 'filetype', 'extension']
			const { mediaData } = await searchFetchMedia(
				vhostId ? parseInt(vhostId.value) : 0,
				{ query },
				'upload DESC',
				0,
				12,
				fields
			)
			mobileSearchResults.value = mediaData
		} catch (e) {
			console.error('Search error:', e)
		} finally {
			mobileSearchLoading.value = false
		}
	}, 300)
}

const clearMobileSearch = () => {
	mobileSearchQuery.value = ''
	mobileSearchResults.value = []
	mobileSearchOpen.value = false
}

const selectMobileSearchResult = (mediaItem: Media) => {
	clearMobileSearch()
	router.push('/media/' + mediaItem.id)
}
const widenFieldMode = ref<Record<string, 'replace' | 'append'>>({})
const bulkApplying = ref(false)
const publishing = ref(false)

// Bulk custom metadata state
const bulkMetaLoading = ref(false)
const bulkMetaProviders = ref<any[]>([])
const bulkMetaGroups = ref<any[]>([])
const bulkCustomMeta = reactive<Record<string, any>>({})
let bulkMetaLoaded = false

const handleCloneModeSwitch = async (mode: string) => {
	cloneMode.value = mode
	if (mode === 'custom' && !bulkMetaLoaded) {
		await loadBulkCustomMeta()
	}
}

const loadBulkCustomMeta = async () => {
	const vid = vhostId?.value ? parseInt(vhostId.value) : 0
	if (!vid) return
	bulkMetaLoading.value = true
	try {
		const [providers, fields, groupings] = await Promise.all([
			getCustomMetaProviders(vid),
			getCustomMeta(vid),
			getCustomMetaGroupings(vid),
		])
		bulkMetaProviders.value = providers
		// Organize fields into groups per provider (same as MediaMetaData)
		const grouped: any[] = []
		const providerSet = new Set(providers.map((p: any) => p.provider))
		for (const prov of providerSet) {
			const provFields = fields.filter((f: any) => f.provider === prov)
			const groupMap: Record<string, any[]> = {}
			for (const f of provFields) {
				const gName = f.grouping || 'General'
				if (!groupMap[gName]) groupMap[gName] = []
				groupMap[gName].push(f)
			}
			for (const [gName, items] of Object.entries(groupMap)) {
				grouped.push({ provider: prov, groupName: gName, items })
			}
		}
		bulkMetaGroups.value = grouped
		bulkMetaLoaded = true
	} catch (err) {
		console.error('Error loading custom meta for bulk edit:', err)
	} finally {
		bulkMetaLoading.value = false
	}
}

const onBulkMetaChange = ({ key, value }: { key: string; value: unknown }) => {
	bulkCustomMeta[key] = value
}

// Bulk moderation actions
const bulkApprove = async () => {
	if (selectedMedia.value.length === 0) return
	try {
		for (const media of selectedMedia.value) {
			await setModerationStatus(media.id, 1)
		}
		toast.success(`Approved ${selectedMedia.value.length} items`)
		selectedMedia.value = []
		refresh.value++
	} catch (err) {
		toast.error('Failed to approve items')
	}
}

const bulkDeny = async () => {
	if (selectedMedia.value.length === 0) return
	try {
		for (const media of selectedMedia.value) {
			await setModerationStatus(media.id, 2)
		}
		toast.success(`Denied ${selectedMedia.value.length} items`)
		selectedMedia.value = []
		refresh.value++
	} catch (err) {
		toast.error('Failed to deny items')
	}
}

const bulkTrash = async () => {
	if (selectedMedia.value.length === 0) return
	try {
		for (const media of selectedMedia.value) {
			await trashFile(media.id)
		}
		toast.success(`${mmMode.value ? 'Archived' : 'Trashed'} ${selectedMedia.value.length} items`)
		selectedMedia.value = []
		refresh.value++
	} catch (err) {
		toast.error(`Failed to ${mmMode.value ? 'archive' : 'trash'} items`)
	}
}

const bulkReconvert = async () => {
	if (selectedMedia.value.length === 0) return
	try {
		for (const media of selectedMedia.value) {
			await queueReconversion(media.id)
		}
		toast.success(`Queued ${selectedMedia.value.length} items for re-conversion`)
		selectedMedia.value = []
	} catch (err) {
		toast.error('Failed to queue re-conversion')
	}
}

const publishToTransfer = async (transferId: number, transferName: string) => {
	if (selectedMedia.value.length === 0) return
	publishing.value = true
	try {
		const { startTransfer } = useTransfers()
		const mediaIds = selectedMedia.value.map(m => m.id)
		const success = await startTransfer(mediaIds, transferId, Number(vhostId?.value || 0))
		if (success) {
			toast.success(`Publishing ${mediaIds.length} items to ${transferName}`)
			selectedMedia.value = []
		} else {
			toast.error('Transfer failed to start')
		}
	} catch (err) {
		toast.error('Failed to publish')
	} finally {
		publishing.value = false
	}
}

const applyBulkUpdate = async () => {
	if (selectedMedia.value.length === 0) return
	bulkApplying.value = true
	try {
		for (const media of selectedMedia.value) {
			const updates: Record<string, any> = {}
			if (cloneMode.value === 'platform' && cloneItem.id) {
				if (cloneFields.value.includes('title' as never)) updates.title = cloneItem.title
				if (cloneFields.value.includes('message' as never)) updates.message = cloneItem.message
				if (cloneFields.value.includes('tags' as never)) updates.tags = cloneItem.tags
				if (cloneFields.value.includes('metadata' as never)) updates.metadata = cloneItem.metadata
				if (cloneFields.value.includes('uid' as never)) updates.uid = cloneItem.uid
			} else if (cloneMode.value === 'widen') {
				const widenIds = Object.keys(widenData.value)
				if (widenIds.length > 0) {
					const wData = widenData.value[widenIds[0]]
					if (wData) {
						if (wData.title) updates.title = widenFieldMode.value.title === 'append' ? `${media.title} ${wData.title}` : wData.title
						if (wData.description) updates.message = widenFieldMode.value.description === 'append' ? `${media.message} ${wData.description}` : wData.description
						if (wData.tags) updates.tags = widenFieldMode.value.tags === 'append' ? `${media.tags || ''}, ${wData.tags}` : wData.tags
					}
				}
			} else if (cloneMode.value === 'manual') {
				if (manualTitle.value) updates.title = manualTitle.value
				if (manualDescription.value) updates.message = manualDescription.value
				if (manualTags.value) updates.tags = manualTags.value
			} else if (cloneMode.value === 'custom') {
				const customKeys = Object.keys(bulkCustomMeta)
				if (customKeys.length > 0) {
					updates.metadata = { user: { ...bulkCustomMeta } }
				}
			}
			if (Object.keys(updates).length > 0) {
				await gqlUpdateFile(media.id, updates)
			}
		}
		toast.success(`Updated ${selectedMedia.value.length} items`)
	} catch (error) {
		console.error('Error applying bulk update:', error)
		toast.error('Failed to apply bulk updates')
	} finally {
		bulkApplying.value = false
	}
}

const userInfo = inject<Ref<Member> | null>('userInfo', null);
const transferList = inject<Ref<TransferDestination[]>>('transferList', ref([]));
const vhostId = inject<Ref<string> | null>('vhostId', null);
const mmMode = inject<Ref<boolean>>('mmMode', ref(false));
const customTabs = ref<Tab[]>([]);

const formatLargeNumber = (num: number): string => num.toLocaleString('en-US');

const isTransferSupported = (transferItem: TransferDestination): boolean => {
	if (transferItem.filetype === 0) return true;
	return selectedMedia.value.every(media => media.filetype === transferItem.filetype);
};

const applyFilters = (setFilterCookie: boolean) => {
	if (setFilterCookie) setCookies();
}

const getWidenData = async () => {
	try {
		if (vhostId) {
			const data = await WidenService.getWidenData(widenSource.value, vhostId.value);
			widenData.value = data;
		}
	} catch (error) {
		console.error("Error fetching Widen data:", error);
	} finally {
		loading.value = false;
	}
};

const setCookies = () => {
	if (vhostId && vhostId.value) {
		if (filters) {
			cookies.set('newSparkFilters' + vhostId.value, JSON.stringify(filters), { maxAge: 86400 * 7, path: "/" });
		}
		cookies.set('newSparkSort' + vhostId.value, sortOrder.value, { maxAge: 86400 * 7, path: "/" });
	}
};

const mergedFilters = computed(() => ({ ...defaultFilters, ...filters }));

const fetchGroupTree = async (vhost: string) => {
	if (vhost) await getGroupTree(vhost);
};

const getCustomTabs = async (userId: string, vhost: string): Promise<void> => {
	// Build default tabs first so they're always available even if getTabs fails
	let defaultTabs: Tab[] = [];
	if (mmMode.value) {
		defaultTabs = [
			{ id: "-1", title: "All", settings: { filters: { moderationStatus: "-1" } }, disabled: 0 },
			{ id: "100", title: "Archived", settings: { filters: { moderationStatus: "100" } }, disabled: 0 }
		];
	} else {
		defaultTabs = [
			{ id: "0", title: "Unmoderated", settings: { filters: { moderationStatus: "0" } }, disabled: 0 },
			{ id: "1", title: "Approved", settings: { filters: { moderationStatus: "1" } }, disabled: 0 },
			{ id: "2", title: "Denied", settings: { filters: { moderationStatus: "2" } }, disabled: 0 },
			{ id: "100", title: "Trashed", settings: { filters: { moderationStatus: "100" } }, disabled: 0 },
			{ id: "-1", title: "All", settings: { filters: { moderationStatus: "-1" } }, disabled: 0 },
		];
	}

	let tabsResult: Tab[] = [];
	try {
		tabsResult = await gqlGetTabs(Number(vhost));
	} catch (error) {
		// getTabs may not be available yet — use defaults only
	}

	customTabs.value = [...defaultTabs, ...tabsResult];
	if (mmMode.value) {
		tab.value = "-1"
	}
};

const selectedGroup = ref<number | null>(null);
const parentGroup = ref<number | null>(null);

watch(() => props.filters, (newFilters) => { Object.assign(filters, { ...newFilters }); }, { deep: true });
watch(mobileView, (newView) => {
	cookies.set('newSparkMobileView', newView, { maxAge: 86400 * 30, path: '/' });
});
watch(mediaView, (newView) => {
	// Also persist the grid/list preference alongside the mobile sub-view
	if (vhostId?.value) {
		cookies.set('newSparkMediaView' + vhostId.value, newView, { maxAge: 86400 * 30, path: '/' });
	}
});
watch(filters, () => {
	if (vhostId && vhostId.value) {
		cookies.set('newSparkFilters' + vhostId.value, JSON.stringify(filters), { maxAge: 86400 * 7, path: "/" });
	}
}, { deep: true });
watch(tab, (newTabIndex) => {
	const newTab = customTabs.value.find(item => item.id == newTabIndex);
	if (newTab && newTab.settings) Object.assign(filters, { ...newTab.settings.filters });
	// Persist the active tab
	if (vhostId?.value) {
		cookies.set('newSparkTab' + vhostId.value, newTabIndex, { maxAge: 86400 * 30, path: '/' });
	}
});
watch(sortOrder, () => {
	if (vhostId && vhostId.value) {
		cookies.set('newSparkSort' + vhostId.value, sortOrder.value, { maxAge: 86400 * 7, path: "/" });
	}
});

const onDragOver = () => { showGroups.value = !showGroups.value; };

const setGroupFilter = (id: any) => {
	if (id && id > 0) {
		selectedGroup.value = id;
		filters.groupid = id;
	} else {
		selectedGroup.value = 0;
		delete filters.groupid
	}
};
const setTotalCount = (count: number) => { totalCount.value = count; }
const setCurrentCount = (count: number) => { currentCount.value = count; }
const cloneSelect = (mediaItem: Media) => { Object.assign(cloneItem, { ...mediaItem }); }
const searchSelect = (mediaItem: Media) => { router.push('/media/' + mediaItem.id); }
const setDragging = (dragging: any) => { isDragging.value = dragging; }

const toggleGroupList = () => {
	showGroups.value = !showGroups.value;
	cookies.set('groupMenu', showGroups.value, { maxAge: 86400 * 30, path: '/' });
};

const initMediaPage = async () => {
	if (!vhostId?.value) return;
	sortOrder.value = 'upload DESC';

	fetchGroupTree(vhostId.value);

	if (userInfo?.value) {
		await getCustomTabs(userInfo.value.id, vhostId.value);
	} else {
		// Set default tabs immediately even without userInfo
		if (mmMode.value) {
			customTabs.value = [
				{ id: "-1", title: "All", settings: { filters: { moderationStatus: "-1" } }, disabled: 0 },
				{ id: "100", title: "Archived", settings: { filters: { moderationStatus: "100" } }, disabled: 0 }
			];
		} else {
			customTabs.value = [
				{ id: "0", title: "Unmoderated", settings: { filters: { moderationStatus: "0" } }, disabled: 0 },
				{ id: "1", title: "Approved", settings: { filters: { moderationStatus: "1" } }, disabled: 0 },
				{ id: "2", title: "Denied", settings: { filters: { moderationStatus: "2" } }, disabled: 0 },
				{ id: "100", title: "Trashed", settings: { filters: { moderationStatus: "100" } }, disabled: 0 },
				{ id: "-1", title: "All", settings: { filters: { moderationStatus: "-1" } }, disabled: 0 },
			];
		}
	}

	sortOrder.value = cookies.get('newSparkSort' + vhostId.value) || 'upload DESC';
	const savedView = cookies.get('newSparkMediaView' + vhostId.value);
	if (savedView === 'grid' || savedView === 'list') mediaView.value = savedView;
	const tmpFilters = cookies.get('newSparkFilters' + vhostId.value);
	Object.assign(filters, { ...structuredClone(defaultFilters), ...tmpFilters });
	if (filters.groupid) selectedGroup.value = filters.groupid;

	// Restore active tab — URL query > cookie > filter match > default
	const queryTab = route.query.tab as string | undefined;
	const savedTab = cookies.get('newSparkTab' + vhostId.value);
	if (queryTab && customTabs.value.find((item) => { return item.id == queryTab })) {
		tab.value = String(queryTab);
	} else if (savedTab !== undefined && savedTab !== null && customTabs.value.find((item) => { return item.id == savedTab })) {
		tab.value = String(savedTab);
	} else if (filters.moderationStatus !== undefined && filters.moderationStatus !== null) {
		const matchingTab = customTabs.value.find((item) => { return item.settings?.filters?.moderationStatus == filters.moderationStatus });
		if (matchingTab) {
			tab.value = matchingTab.id;
		}
	} else {
		tab.value = customTabs.value.length > 0 ? customTabs.value[0].id : "-1";
	}

	// After resolving the tab, explicitly apply its filters.
	// The watch(tab) only fires on VALUE change — if tab resolved to the same
	// initial value (e.g. "0"), the watch never fires and stale cookie filters persist.
	const resolvedTab = customTabs.value.find(item => item.id == tab.value);
	if (resolvedTab?.settings?.filters) {
		Object.assign(filters, { ...resolvedTab.settings.filters });
	}
	// Persist the resolved tab to cookie so it reloads correctly
	cookies.set('newSparkTab' + vhostId.value, tab.value, { maxAge: 86400 * 30, path: '/' });

	const savedGroupMenu = cookies.get('groupMenu');
	if (savedGroupMenu !== undefined && savedGroupMenu !== null) {
		showGroups.value = savedGroupMenu;
	} else {
		showGroups.value = true;
	}
	showMediaList.value = true;
};

onMounted(() => { initMediaPage(); });

// If userInfo wasn't ready at mount, retry tabs when it becomes available
watch(() => userInfo?.value, async (newVal) => {
	if (newVal && vhostId?.value && customTabs.value.length <= 2) {
		await getCustomTabs(newVal.id, vhostId.value);
	}
}, { immediate: false });
</script>

<template>
	<div v-if="userInfo && vhostId && userInfo.permissions" class="flex border-t text-secondary-foreground h-screen">

		<!-- Projects & Folders sidebar (desktop only) -->
		<div v-if="(showGroups || isDragging)" class="hidden md:flex border-r w-[240px] shrink-0 flex-col bg-background">
			<div class="flex items-center justify-between px-3 py-2.5 border-b">
				<span class="text-sm font-medium">Projects & Folders</span>
				<button @click="toggleGroupList()" class="p-1 rounded hover:bg-muted text-muted-foreground">
					<PanelLeftClose class="h-4 w-4" />
				</button>
			</div>
			<div class="px-3 py-2">
				<Button size="sm" variant="outline" class="w-full gap-1.5 text-xs">
					<Plus class="h-3.5 w-3.5" /> New Folder
				</Button>
			</div>
			<div class="flex-1 overflow-y-auto px-2 pb-4">
				<button class="flex items-center gap-2 w-full px-2 py-1.5 rounded text-sm hover:bg-muted transition-colors"
					:class="{ 'bg-muted font-medium': !selectedGroup || selectedGroup === 0 }"
					@click.prevent="setGroupFilter(0)">
					<Home class="h-3.5 w-3.5 text-muted-foreground" /> All Assets
				</button>
				<div class="mt-1">
					<TreeView :groupTree="groupTree" :selectedGroup="selectedGroup || undefined" :parentGroup="parentGroup || undefined" @select-group="setGroupFilter" />
				</div>
			</div>
		</div>

		<!-- Collapsed sidebar toggle (desktop only) -->
		<div v-if="!showGroups && !isDragging" class="hidden md:flex border-r shrink-0 flex-col items-center py-3 w-10" @dragover="onDragOver">
			<button @click="toggleGroupList()" class="p-1.5 rounded hover:bg-muted text-muted-foreground" title="Open folders">
				<PanelLeftOpen class="h-4 w-4" />
			</button>
		</div>

		<!-- Projects & Folders Sheet (mobile only) -->
		<Sheet v-model:open="mobileGroupsOpen">
			<SheetContent side="left" class="w-[280px] p-0 flex flex-col">
				<SheetHeader class="px-3 py-2.5 border-b">
					<SheetTitle class="text-sm font-medium">Projects & Folders</SheetTitle>
				</SheetHeader>
				<div class="px-3 py-2">
					<Button size="sm" variant="outline" class="w-full gap-1.5 text-xs">
						<Plus class="h-3.5 w-3.5" /> New Folder
					</Button>
				</div>
				<div class="flex-1 overflow-y-auto px-2 pb-4">
					<button class="flex items-center gap-2 w-full px-2 py-1.5 rounded text-sm hover:bg-muted transition-colors"
						:class="{ 'bg-muted font-medium': !selectedGroup || selectedGroup === 0 }"
						@click.prevent="setGroupFilter(0); mobileGroupsOpen = false">
						<Home class="h-3.5 w-3.5 text-muted-foreground" /> All Assets
					</button>
					<div class="mt-1">
						<TreeView :groupTree="groupTree" :selectedGroup="selectedGroup || undefined" :parentGroup="parentGroup || undefined" @select-group="(id: any) => { setGroupFilter(id); mobileGroupsOpen = false }" />
					</div>
				</div>
			</SheetContent>
		</Sheet>

		<!-- Main content -->
		<div class="flex-1 flex flex-col min-w-0 bg-card">

			<!-- Status tabs (desktop) -->
			<div class="hidden md:block bg-background border-b">
				<div class="flex items-center px-4 gap-1 pt-1">
					<button v-for="menuItem in customTabs" :key="menuItem.id"
						class="px-4 py-2.5 text-sm font-medium transition-colors whitespace-nowrap rounded-t-md border border-b-0"
						:class="tab === menuItem.id
							? 'bg-card text-foreground border-border -mb-px z-10'
							: 'bg-transparent text-muted-foreground hover:text-foreground border-transparent hover:bg-muted/50'"
						@click.prevent="tab = menuItem.id; if (menuItem.settings?.filters) Object.assign(filters, { ...menuItem.settings.filters }); refresh++">
						{{ menuItem.title }}
					</button>
				</div>
			</div>

			<!-- Mobile header bar -->
			<div class="md:hidden bg-background border-b sticky top-0 z-50">
				<div class="flex items-center justify-between px-3 py-2 gap-2">
					<!-- Left: folder + tab select -->
					<div class="flex items-center gap-2 min-w-0">
						<button class="p-1.5 rounded hover:bg-muted flex-shrink-0" @click="mobileGroupsOpen = true">
							<FolderTree class="h-4 w-4" />
						</button>
						<Select :model-value="tab" @update:model-value="(val: string) => { tab = val; const t = customTabs.find(item => item.id == val); if (t?.settings?.filters) Object.assign(filters, { ...t.settings.filters }); refresh++ }">
							<SelectTrigger class="h-8 text-xs bg-card min-w-0 flex-1">
								<SelectValue placeholder="Filter" />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									<SelectItem v-for="menuItem in customTabs" :key="menuItem.id" :value="menuItem.id">{{ menuItem.title }}</SelectItem>
								</SelectGroup>
							</SelectContent>
						</Select>
					</div>
					<!-- Right: view toggle -->
					<div class="flex items-center gap-1 flex-shrink-0">
						<button class="p-1.5 rounded border transition-colors" :class="mobileView === 'scroll' ? 'bg-primary text-primary-foreground border-primary' : 'border-transparent hover:bg-muted'" @click="mobileView = 'scroll'" title="Feed">
							<GalleryVertical class="h-4 w-4" />
						</button>
						<button class="p-1.5 rounded border transition-colors" :class="mobileView === 'swipe' ? 'bg-primary text-primary-foreground border-primary' : 'border-transparent hover:bg-muted'" @click="mobileView = 'swipe'" title="Tinder">
							<Layers class="h-4 w-4" />
						</button>
					</div>
				</div>
			</div>

			<!-- Desktop Toolbar -->
			<div class="hidden md:block bg-background border-b sticky top-0 z-50">
				<div class="flex items-center justify-between px-4 py-2 gap-3">
					<!-- Left: actions -->
					<div class="flex items-center gap-2">
						<Sheet>
							<!-- Actions dropdown -->
							<DropdownMenu>
								<DropdownMenuTrigger as-child>
									<Button variant="outline" size="sm" :disabled="selectedMedia.length === 0" class="gap-1.5 text-xs">
										<Paintbrush class="h-3.5 w-3.5" /> Actions <ChevronDown class="h-3 w-3" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent>
									<DropdownMenuItem v-if="tab !== '1'" class="text-xs gap-2" @click="bulkApprove"><Check class="h-3.5 w-3.5" /> Approve</DropdownMenuItem>
									<DropdownMenuItem v-if="tab !== '2'" class="text-xs gap-2" @click="bulkDeny"><X class="h-3.5 w-3.5" /> Deny</DropdownMenuItem>
									<DropdownMenuItem class="text-xs gap-2" @click="bulkTrash"><Trash2 class="h-3.5 w-3.5" /> {{ mmMode ? 'Archive' : 'Trash' }}</DropdownMenuItem>
									<DropdownMenuItem class="text-xs gap-2" @click="bulkReconvert"><RefreshCw class="h-3.5 w-3.5" /> Re-convert</DropdownMenuItem>
									<DropdownMenuSeparator />
									<SheetTrigger class="w-full">
										<DropdownMenuItem class="text-xs gap-2" @click.prevent="sheetMode = 'clone'"><Copy class="h-3.5 w-3.5" /> Update Metadata</DropdownMenuItem>
									</SheetTrigger>
									<DropdownMenuSub v-if="transferList.length > 0 && transferList.some(isTransferSupported)">
										<DropdownMenuSubTrigger class="text-xs gap-2"><ArrowUpFromLine class="h-3.5 w-3.5" /> Transfer To</DropdownMenuSubTrigger>
										<DropdownMenuSubContent>
											<template v-for="(transfer, index) in transferList" :key="index">
												<DropdownMenuItem v-if="isTransferSupported(transfer)" class="text-xs" @click="publishToTransfer(transfer.id, transfer.name)">{{ transfer.name }}</DropdownMenuItem>
											</template>
										</DropdownMenuSubContent>
									</DropdownMenuSub>
									<DropdownMenuSub>
										<DropdownMenuSubTrigger v-if="transferList.length > 0" class="text-xs gap-2"><Share2 class="h-3.5 w-3.5" /> Share</DropdownMenuSubTrigger>
										<DropdownMenuSubContent>
											<SheetTrigger class="w-full">
												<DropdownMenuItem @click.prevent="sheetMode = 'share'" class="text-xs">For Review</DropdownMenuItem>
											</SheetTrigger>
											<DropdownMenuItem class="text-xs">Asset</DropdownMenuItem>
										</DropdownMenuSubContent>
									</DropdownMenuSub>
								</DropdownMenuContent>
							</DropdownMenu>

							<!-- Metadata update sheet — bottom drawer -->
							<SheetContent side="bottom" class="h-[75vh] flex flex-col rounded-t-xl">
								<SheetHeader class="shrink-0 pb-2 border-b pr-10">
									<div class="flex items-center justify-between">
										<div>
											<SheetTitle class="text-base">
												<span v-if="sheetMode === 'clone'">Update Metadata</span>
												<span v-else-if="sheetMode === 'bulk'">Bulk Updates</span>
												<span v-else-if="sheetMode === 'share'">Share Media</span>
											</SheetTitle>
											<SheetDescription v-if="sheetMode === 'clone'" class="text-xs mt-0.5">
												Apply metadata changes to {{ selectedMedia.length }} selected item{{ selectedMedia.length !== 1 ? 's' : '' }}
											</SheetDescription>
										</div>
										<div class="flex items-center gap-2">
											<Button size="sm" @click.prevent="applyBulkUpdate" :disabled="bulkApplying" class="text-xs h-8">
												{{ bulkApplying ? 'Applying...' : `Apply to ${selectedMedia.length} items` }}
											</Button>
											<SheetClose as-child>
												<Button size="sm" variant="outline" class="text-xs h-8">Cancel</Button>
											</SheetClose>
										</div>
									</div>
									<!-- Mode tabs — inline in header -->
									<div v-if="sheetMode === 'clone'" class="flex items-center gap-1 mt-2">
										<button v-for="mode in ['platform', 'widen', 'manual', 'custom']" :key="mode"
											class="relative px-3 py-1.5 text-xs font-medium capitalize transition-colors rounded-md"
											:class="cloneMode === mode ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'"
											@click.prevent="handleCloneModeSwitch(mode)">
											{{ mode === 'custom' ? 'Custom Fields' : mode }}
										</button>
									</div>
								</SheetHeader>

								<!-- Scrollable body -->
								<div class="flex-1 overflow-y-auto py-4 px-4">
									<div v-if="sheetMode === 'clone'">

										<!-- Platform mode — clone from existing media -->
										<div v-if="cloneMode === 'platform'" class="space-y-4">
											<MediaSearchField v-model="queryTerm" :vhost="vhostId" @on-select="cloneSelect" />
											<div>
												<p class="text-sm font-medium mb-2">Clone fields:</p>
												<div class="flex flex-wrap gap-4">
													<div v-for="field in ['title', 'message', 'tags', 'metadata', 'uid']" :key="field" class="flex items-center gap-2">
														<Switch v-model="cloneFields" :value="field" />
														<Label class="text-xs capitalize">{{ field === 'message' ? 'Description' : field === 'uid' ? 'User ID' : field }}</Label>
													</div>
												</div>
											</div>
											<div v-if="cloneItem.id" class="flex gap-4 border rounded-md p-3">
												<div class="w-32 shrink-0">
													<media-thumb :mediaItem="cloneItem" :show-transfers="true" :profile="11" :key="cloneItem.id" />
												</div>
												<div class="text-sm space-y-1">
													<div><span class="text-muted-foreground">Title:</span> {{ cloneItem.title }}</div>
													<div><span class="text-muted-foreground">Description:</span> {{ cloneItem.message }}</div>
													<div><span class="text-muted-foreground">User ID:</span> {{ cloneItem.uid }}</div>
												</div>
											</div>
										</div>

										<!-- Widen mode -->
										<div v-else-if="cloneMode === 'widen'" class="space-y-3">
											<div>
												<Label class="text-xs">Widen Source IDs (comma-separated)</Label>
												<Input v-model="widenSource" class="mt-1" />
											</div>
											<Button v-if="widenSource" size="sm" @click.prevent="getWidenData()">Retrieve Data</Button>
											<div v-if="Object.keys(widenData).length > 0" class="space-y-3">
												<div v-for="(wData, wId) in widenData" :key="wId" class="border rounded-md p-3 space-y-2">
													<p class="text-xs font-medium">Widen ID: {{ wId }}</p>
													<div v-if="wData.title" class="flex items-center justify-between text-sm">
														<div><span class="text-muted-foreground text-xs">Title:</span> {{ wData.title }}</div>
														<Select v-model="widenFieldMode.title">
															<SelectTrigger class="w-24 h-7 text-xs"><SelectValue placeholder="Replace" /></SelectTrigger>
															<SelectContent>
																<SelectItem value="replace">Replace</SelectItem>
																<SelectItem value="append">Append</SelectItem>
															</SelectContent>
														</Select>
													</div>
													<div v-if="wData.description" class="flex items-center justify-between text-sm">
														<div><span class="text-muted-foreground text-xs">Description:</span> {{ wData.description?.substring(0, 80) }}...</div>
														<Select v-model="widenFieldMode.description">
															<SelectTrigger class="w-24 h-7 text-xs"><SelectValue placeholder="Replace" /></SelectTrigger>
															<SelectContent>
																<SelectItem value="replace">Replace</SelectItem>
																<SelectItem value="append">Append</SelectItem>
															</SelectContent>
														</Select>
													</div>
													<div v-if="wData.tags" class="flex items-center justify-between text-sm">
														<div><span class="text-muted-foreground text-xs">Tags:</span> {{ wData.tags }}</div>
														<Select v-model="widenFieldMode.tags">
															<SelectTrigger class="w-24 h-7 text-xs"><SelectValue placeholder="Replace" /></SelectTrigger>
															<SelectContent>
																<SelectItem value="replace">Replace</SelectItem>
																<SelectItem value="append">Append</SelectItem>
															</SelectContent>
														</Select>
													</div>
												</div>
											</div>
										</div>

										<!-- Manual mode — 2-column grid -->
										<div v-else-if="cloneMode === 'manual'" class="grid grid-cols-1 md:grid-cols-2 gap-4">
											<div>
												<Label class="text-xs">Title</Label>
												<Input v-model="manualTitle" placeholder="Leave empty to skip" class="mt-1" />
											</div>
											<div>
												<Label class="text-xs">Tags</Label>
												<Input v-model="manualTags" placeholder="Comma-separated tags" class="mt-1" />
											</div>
											<div class="md:col-span-2">
												<Label class="text-xs">Description</Label>
												<textarea v-model="manualDescription" placeholder="Leave empty to skip"
													rows="3"
													class="mt-1 flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
											</div>
										</div>

										<!-- Custom Fields mode — full metadata editor -->
										<div v-else-if="cloneMode === 'custom'">
											<div v-if="bulkMetaLoading" class="flex items-center justify-center py-12 text-sm text-muted-foreground gap-2">
												<Loader2 class="h-4 w-4 animate-spin" /> Loading custom fields...
											</div>
											<CustomMetaData
												v-else-if="bulkMetaProviders.length > 0"
												:providers="bulkMetaProviders"
												:groups="bulkMetaGroups"
												:metadata="bulkCustomMeta"
												surface="sheet"
												@change="onBulkMetaChange"
											/>
											<div v-else class="text-center py-8 text-sm text-muted-foreground">
												No custom metadata fields configured for this project.
											</div>
										</div>
									</div>
								</div>
							</SheetContent>
							</Sheet>

						<!-- Upload dropdown -->
						<Sheet>
							<DropdownMenu>
								<DropdownMenuTrigger as-child>
									<Button variant="outline" size="sm" class="gap-1.5 text-xs">
										<Upload class="h-3.5 w-3.5" /> Upload <ChevronDown class="h-3 w-3" />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent>
									<SheetTrigger class="w-full">
										<DropdownMenuItem class="text-xs gap-2" @click.prevent="sheetMode = 'directupload'"><CloudUpload class="h-3.5 w-3.5" /> Direct Upload</DropdownMenuItem>
									</SheetTrigger>
									<SheetTrigger class="w-full">
										<DropdownMenuItem class="text-xs gap-2" @click.prevent="sheetMode = 'shareuploader'"><Link class="h-3.5 w-3.5" /> Shared Uploader</DropdownMenuItem>
									</SheetTrigger>
									<template v-if="!mmMode">
										<SheetTrigger class="w-full">
											<DropdownMenuItem class="text-xs gap-2" @click.prevent="sheetMode = 'widen'"><FileOutput class="h-3.5 w-3.5" /> Import from Widen</DropdownMenuItem>
										</SheetTrigger>
										<SheetTrigger class="w-full">
											<DropdownMenuItem class="text-xs gap-2" @click.prevent="sheetMode = 'rss'"><Rss class="h-3.5 w-3.5" /> Import from RSS</DropdownMenuItem>
										</SheetTrigger>
									</template>
								</DropdownMenuContent>
							</DropdownMenu>

							<SheetContent side="top" class="max-h-[80vh] overflow-y-auto">
								<SheetHeader>
									<SheetTitle>
										<span v-if="sheetMode === 'directupload'">Upload Media</span>
										<span v-else-if="sheetMode === 'widen'">Import from Widen</span>
										<span v-else-if="sheetMode === 'rss'">Import from RSS</span>
										<span v-else-if="sheetMode === 'shareuploader'">Shared Uploader</span>
									</SheetTitle>
								</SheetHeader>
								<div class="py-4">
									<Uploader v-if="sheetMode === 'directupload'" />
									<WidenImport v-if="sheetMode === 'widen'" />
									<CustomMetaData v-if="sheetMode === 'rss'" />
								</div>
								<SheetFooter>
									<SheetClose as-child>
										<Button size="sm" variant="outline">Close</Button>
									</SheetClose>
								</SheetFooter>
							</SheetContent>
						</Sheet>

						<DropdownMenu v-if="transferList.length > 0">
						<DropdownMenuTrigger as-child>
							<Button variant="outline" size="sm" :disabled="selectedMedia.length === 0" class="gap-1.5 text-xs">
								<ArrowUpFromLine class="h-3.5 w-3.5" /> Publish <ChevronDown class="h-3 w-3" />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent>
							<DropdownMenuLabel class="text-xs text-muted-foreground">Transfer Destinations</DropdownMenuLabel>
							<DropdownMenuSeparator />
							<template v-for="(transfer, index) in transferList" :key="index">
								<DropdownMenuItem v-if="isTransferSupported(transfer)" class="text-xs gap-2" @click="publishToTransfer(transfer.id, transfer.name)">
									<CloudUpload class="h-3.5 w-3.5" /> {{ transfer.name }}
								</DropdownMenuItem>
							</template>
						</DropdownMenuContent>
					</DropdownMenu>
						<Button v-if="!mmMode" variant="outline" size="sm" class="gap-1.5 text-xs"><FileOutput class="h-3.5 w-3.5" /> Export</Button>

						<!-- Selection indicator -->
						<span v-if="selectedMedia.length > 0" class="text-xs text-muted-foreground ml-1 tabular-nums">
							{{ selectedMedia.length }} selected
						</span>
					</div>

					<!-- Right: count, sort, view toggle -->
					<div class="flex items-center gap-3">
						<span v-if="currentCount > 0 && totalCount > 0" class="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
							{{ currentCount }} of {{ formatLargeNumber(totalCount) }}
						</span>

						<Select v-model="sortOrder" @select="applyFilters(false)">
							<SelectTrigger class="h-8 w-44 text-xs bg-card">
								<SelectValue placeholder="Sort order" />
							</SelectTrigger>
							<SelectContent>
								<SelectGroup>
									<SelectItem value="title ASC">Title A-Z</SelectItem>
									<SelectItem value="title DESC">Title Z-A</SelectItem>
									<SelectItem value="upload ASC">Oldest first</SelectItem>
									<SelectItem value="upload DESC">Newest first</SelectItem>
									<SelectItem value="hits ASC">Least views</SelectItem>
									<SelectItem value="hits DESC">Most views</SelectItem>
									<SelectItem value="filesize ASC">Smallest</SelectItem>
									<SelectItem value="filesize DESC">Largest</SelectItem>
									<SelectItem value="commentcount DESC">Most comments</SelectItem>
									<SelectItem value="rating DESC">Highest rated</SelectItem>
									<SelectItem value="shares DESC">Most shared</SelectItem>
									<SelectItem value="date ASC">Taken date (oldest)</SelectItem>
									<SelectItem value="date DESC">Taken date (newest)</SelectItem>
									<SelectItem value="votecount DESC">Most votes</SelectItem>
								</SelectGroup>
							</SelectContent>
						</Select>

						<div class="flex items-center border rounded-md overflow-hidden">
							<button class="p-1.5 transition-colors" :class="mediaView === 'grid' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'" @click="mediaView = 'grid'">
								<LayoutGrid class="h-4 w-4" />
							</button>
							<button class="p-1.5 transition-colors" :class="mediaView === 'list' ? 'bg-primary text-primary-foreground' : 'hover:bg-muted'" @click="mediaView = 'list'">
								<List class="h-4 w-4" />
							</button>
						</div>
					</div>
				</div>
			</div>

			<!-- Media grid/list -->
			<div class="flex-1 overflow-y-auto p-4">
				<MediaList v-if="vhostId !== null && showMediaList"
					v-model="selectedMedia"
					:filters="filters"
					:type="mediaView"
					:vhostId="vhostId"
					:sort-order="sortOrder"
					:mobile-view="mobileView"
					@update-dragging="setDragging"
					@set-total-count="setTotalCount"
					@set-current-count="setCurrentCount"
					default-profile="11"
					:refreshKey="refresh" />
			</div>
		</div>
	</div>

	<!-- Error dialog -->
	<AlertDialog v-model:open="showDialog">
		<AlertDialogContent class="sm:max-w-md">
			<AlertDialogHeader>
				<AlertDialogTitle>{{ dialogTitle }}</AlertDialogTitle>
				<AlertDialogDescription>{{ dialogMessage }}</AlertDialogDescription>
			</AlertDialogHeader>
			<AlertDialogFooter>
				<AlertDialogCancel @click="showDialog = false">Close</AlertDialogCancel>
			</AlertDialogFooter>
		</AlertDialogContent>
	</AlertDialog>
</template>
